/**
 * A song's PARTS (2026-10-21, operator: "the whole sections thing is confusing, needs to be simpler and more
 * intuitive"; study `public/sections-study.html`, rules `timeline/songParts.ts`). Was the section marker
 * (2026-10-13, "needs a good way to mark the sections in a new track").
 *
 * The song cut into parts: every bar in exactly one part, a part named (INTRO, VERSE 2...) or not yet. In a timeline
 * the parts ARE its sections (`timeline/songFollow.ts`); in the Audio library they are the song's own.
 * Gestures: drag across unmarked song to make a part, then say what it is (a menu: the five, keys 1-5, or Other);
 * drag a part to slide it through unmarked song; drag the line between two parts to move it; click a part for its menu
 * (rename, play it looping, split at the playhead, join, not a part); click unmarked song, or the bar numbers, to hear
 * it from there on the computer and output the timeline plays it on. Numbers are never typed: a second VERSE makes
 * them VERSE 1 and VERSE 2. Esc takes a drag back, and a part just dragged out goes if it is never named, with
 * anything it cut. Keys once the row was touched: 1-5 name the chosen part, Space plays or stops, S splits at the
 * playhead, \ fits. Every change is one undo step (the song history, `testTrackStore.ts`).
 *
 * Guides drawn above the waveform: an ENERGY lane (the analyser's loudness per bar) and the stretches where someone
 * SINGS (its vocal ranges), with bar numbers and phrase lines every 4 bars (beat lines when zoomed in); an overview
 * strip under it shows the whole song and the part in view (drag it to scroll, click to jump).
 *
 * Zoom and scroll as in OPCut (operator, 2026-10-14: "needs the zoom in, scroll behaviour like in opcut"; OPCut v3
 * `crates/app/src/timeline.rs` + `ui/app.slint`): the wheel zooms about the pointer, 1.5x per notch (60 px of
 * deltaY), the bar under the pointer staying put; Shift+wheel, or a sideways trackpad scroll, scrolls 10% of the view
 * per notch; `\` fits. On a touch screen: pinch zooms, two fingers drag to scroll. Unlike OPCut, the view pages to
 * follow the playhead while the song plays here (a wheel turn holds it still for a moment).
 *
 * Tried and dropped (2026-10-13): suggesting the parts from loudness. On Danger Zone and Coming Undone the 4-bar
 * loudness is flat across sections, so a suggestion would confidently mark the wrong bars.
 */
import { createEffect, createMemo, createSignal, For, onCleanup, onMount, Show, untrack, type JSX } from 'solid-js';
import { Portal } from 'solid-js/web';
import { beatAt, beatsPerBar, songBars, songTimeAtBar, waveformPath } from '../timeline/testTrack.ts';
import { beatmaps, loadTrack, patchBeatmap, peaksById, play, status, stop } from '../timeline/testTrackStore.ts';
import { primaryBridge } from '../opbridge/store.ts';
import { sectionHue } from '../timeline/sectionHue.ts';
import { pushToast } from '../ui/toast.ts';
import {
  join, kindOf, markRange, marksFromParts, moveLine, OTHER_KINDS, PART_KINDS, partAt, partsFromMarks, setName, slide, slideRange, splitAt,
  type SongPart,
} from '../timeline/songParts.ts';

const hueOf = (name: string | null) => (name ? sectionHue(name) : '#6b7078');
const label = (k: string) => k.charAt(0) + k.slice(1).toLowerCase();

const H_BARS = 16; const H_ENERGY = 26; const H_WAVE = 76; const H_OVERVIEW = 12;
/** OPCut's numbers: 1.5x a notch, a notch = 60 px of wheel; scroll = 10% of the view a notch. */
const ZOOM_STEP = 1.5; const NOTCH_PX = 60; const PAN_FRAC = 0.1;
/** The closest zoom: two bars across the strip. */
const MIN_SPAN = 2;
/** Bar-number spacing: the first step at which labels sit at least this far apart. */
const LABEL_LADDER = [1, 2, 4, 8, 16, 32, 64];
const LABEL_MIN_PX = 30;

export function SongMarkRow(props: {
  /** The song (the audio library, or a timeline's song). */
  trackId: string;
  /** Where "click to listen" plays it: a PC's node id (absent = this app's main OPBridge) and an output. */
  pc?: string;
  output?: string;
  width: number;
  /** The library's Done; a timeline leaves it off (the row is always there). */
  onDone?: () => void;
  /** A timeline's Hide (2026-10-28, operator: "why is songs parts always there?"): folds the row to a thin strip. */
  onHide?: () => void;
}): JSX.Element {
  const id = () => props.trackId || null;
  const map = () => { const t = id(); return t ? beatmaps()[t] : undefined; };
  const peaks = () => { const t = id(); return t ? peaksById()[t] : undefined; };
  onMount(() => { const t = id(); if (t) void loadTrack(t); });
  const bars = () => (map() ? songBars(map()!) : 0);

  // ---- the parts: saved ones, or a drag's preview ----
  const saved = createMemo(() => (map() ? partsFromMarks(map()!.sections, bars()) : []));
  const [preview, setPreview] = createSignal<SongPart[] | null>(null);
  const parts = () => preview() ?? saved();
  /** The chosen part, by its first bar (survives renumbering and edits elsewhere). */
  const [selFrom, setSelFrom] = createSignal<number | null>(null);
  const selIndex = () => { const f = selFrom(); return f == null ? -1 : parts().findIndex((p) => p.from === f); };
  const save = (next: SongPart[]) => {
    const t = id();
    if (!t) return;
    const before = saved();
    void patchBeatmap(t, { sections: marksFromParts(next) });
    // The moment a kind gets its second part, say why the names changed.
    for (const k of new Set(next.filter((p) => p.name).map((p) => kindOf(p.name!)))) {
      const n = next.filter((p) => p.name && kindOf(p.name) === k).length;
      const was = before.filter((p) => p.name && kindOf(p.name) === k).length;
      if (n === 2 && was === 1) pushToast('ok', `Two ${label(k).toLowerCase()}s now, so they are numbered: ${k} 1 and ${k} 2.`, 3500);
    }
  };
  /** The row is in use (it was touched): its keys apply. Pressing elsewhere ends it, so Space plays the timeline.
   *  In the library's sheet (`onDone`) the row is all there is, so it starts in use. */
  const [active, setActive] = createSignal(!!props.onDone);

  // ---- the view: song bars [from, from + span); span null = the whole song (follows the song once it loads) ----
  const [viewFrom, setViewFrom] = createSignal(1);
  const [viewSpan, setViewSpan] = createSignal<number | null>(null);
  const span = () => Math.max(Math.min(MIN_SPAN, bars() || MIN_SPAN), Math.min(viewSpan() ?? bars(), bars() || 1));
  const from = () => (viewSpan() === null ? 1 : viewFrom());
  const fitted = () => viewSpan() === null || span() >= bars() - 1e-6;
  const setView = (f: number, s: number) => {
    const b = bars();
    if (!b) return;
    const sp = Math.max(Math.min(MIN_SPAN, b), Math.min(b, s));
    if (sp >= b - 1e-6) { setViewSpan(null); setViewFrom(1); return; }
    setViewSpan(sp);
    setViewFrom(Math.max(1, Math.min(1 + b - sp, f)));
  };
  const fit = () => { setViewSpan(null); setViewFrom(1); };
  const ppb = () => (bars() ? props.width / span() : 0);
  /** Song bar (1-based, fractional) under a viewport x. */
  const barAtX = (el: HTMLElement, x: number) => Math.max(1, Math.min(bars() + 1, from() + (x - el.getBoundingClientRect().left) / Math.max(1e-6, ppb())));
  const xOfBar = (bar: number) => (bar - from()) * ppb();
  const xOfSec = (sec: number) => { const m = map()!; return xOfBar(1 + beatAt(m, sec) / beatsPerBar(m)); };

  /** Zoom by `notches` (positive = in) keeping the bar at fraction `frac` of the strip where it is. */
  const zoomAt = (frac: number, notches: number) => {
    const s0 = span(); const f0 = from();
    const anchor = f0 + frac * s0;
    const s1 = s0 / Math.pow(ZOOM_STEP, notches);
    const clamped = Math.max(Math.min(MIN_SPAN, bars()), Math.min(bars(), s1));
    setView(anchor - frac * clamped, clamped);
  };
  let lastUserView = 0;
  let stripEl: HTMLDivElement | undefined;
  let rowEl: HTMLDivElement | undefined;
  const onWheel = (e: WheelEvent) => {
    if (!bars() || !stripEl) return;
    e.preventDefault();
    e.stopPropagation();
    lastUserView = Date.now();
    const unit = e.deltaMode === 1 ? 40 : e.deltaMode === 2 ? 800 : 1;
    const dx = e.deltaX * unit; const dy = e.deltaY * unit;
    const r = stripEl.getBoundingClientRect();
    if (Math.abs(dx) > Math.abs(dy) || e.shiftKey) {
      // Windows turns Shift+wheel into deltaX; a trackpad scrolls sideways. Either way: along the song.
      const d = Math.abs(dx) > Math.abs(dy) ? dx : dy;
      const notches = Math.max(-32, Math.min(32, d / NOTCH_PX));
      setView(from() + notches * PAN_FRAC * span(), span());
      return;
    }
    const notches = Math.max(-32, Math.min(32, -dy / NOTCH_PX));
    zoomAt(Math.max(0, Math.min(1, (e.clientX - r.left) / Math.max(1, r.width))), notches);
  };
  onMount(() => { stripEl?.addEventListener('wheel', onWheel, { passive: false }); });
  onCleanup(() => stripEl?.removeEventListener('wheel', onWheel));

  /** A drag across unmarked song, before it is let go. */
  const [draft, setDraft] = createSignal<{ a: number; b: number } | null>(null);
  /** The bar under the pointer, for the hover line (null when the pointer is elsewhere). */
  const [hoverBar, setHoverBar] = createSignal<number | null>(null);
  /** What a press here would do, for the cursor. */
  const [hoverWhat, setHoverWhat] = createSignal<'line' | 'part' | 'none' | 'bars'>('none');
  /** Where a click asked the song to play from (song bar), for Space to play it again. */
  const [cueBar, setCueBar] = createSignal(1);
  const playingHere = () => { const s = status(); return !!s?.playing && s.id === id(); };
  const soundPc = () => props.pc || primaryBridge()?.nodeId || '';
  const playFrom = (bar: number, loop?: { fromBar: number; toBar: number }) => {
    const t = id();
    if (!t || !soundPc()) return;
    setCueBar(bar);
    void play({ id: t, pc: soundPc(), output: props.output ?? '', fromBar: Math.max(1, Math.floor(bar)), ...(loop ? { loop } : {}) });
  };
  const togglePlay = () => { if (playingHere()) void stop(); else playFrom(cueBar()); };
  // The playhead GLIDES, and never steps back. Operator, 2026-10-14: "choppy as hell", then "rubberbanding, not
  // smooth". OPBridge reports the song position (`posSec`) twice a second; a clock runs every frame from it. A reply
  // is only ever LATE (measured on the dev PC: 5 ms typical, 77 ms worst), so a position a little behind the glide
  // is old news, not the glide being ahead: pulling back to it was the rubberband. Each reply now only changes the
  // glide's SPEED (a few percent, closing the gap over about half a second); the line itself stays continuous. Only a
  // gap of 0.4 s or more (a jump, a restart) moves it at once.
  const [anchor, setAnchor] = createSignal<{ at: number; sec: number; rate: number } | null>(null);
  const [frame, setFrame] = createSignal(0);
  const glideSec = (now: number) => { const a = anchor(); return a ? a.sec + Math.min(1.5, Math.max(0, (now - a.at) / 1000)) * a.rate : null; };
  createEffect(() => {
    const s = status();
    if (!s?.playing || s.id !== id() || typeof s.posSec !== 'number') { setAnchor(null); return; }
    const now = performance.now();
    const p = untrack(() => glideSec(now));
    const err = p == null ? Infinity : s.posSec - p;
    if (!Number.isFinite(err) || Math.abs(err) >= 0.4) { setAnchor({ at: now, sec: s.posSec, rate: 1 }); return; }
    // Behind the report: speed up (up to 10%). Ahead of it by under 150 ms: a late reply, ease off barely (3% at most);
    // ahead by more: slow down (up to 10%). Never below 0.9: it always moves forward.
    const rate = err >= 0 ? 1 + Math.min(0.1, err / 0.5)
      : err > -0.15 ? 1 - Math.min(0.03, -err / 2)
      : 1 - Math.min(0.1, -err / 0.5);
    setAnchor({ at: now, sec: p!, rate });
  });
  let raf = 0;
  createEffect(() => {
    if (anchor() && !raf) { const step = () => { setFrame(performance.now()); raf = requestAnimationFrame(step); }; raf = requestAnimationFrame(step); }
    else if (!anchor() && raf) { cancelAnimationFrame(raf); raf = 0; }
  });
  onCleanup(() => { if (raf) cancelAnimationFrame(raf); });
  /** The song beat now (0 = bar 1's downbeat), gliding; null when this song is not playing. */
  const playBeat = () => {
    const m = map();
    if (!m || !playingHere()) return null;
    const sec = glideSec(frame() || performance.now());
    if (sec != null) return beatAt(m, sec);
    const s = status();
    return s?.bar == null ? null : (s.bar - 1) * beatsPerBar(m) + ((s.beat ?? 1) - 1);
  };
  const playBar = () => { const b = playBeat(); return b == null ? null : 1 + b / beatsPerBar(map()!); };
  /** The 4-dot metronome: which beat of the bar is sounding (0-based), or null when stopped. */
  const beatInBar = () => { const b = playBeat(); if (b == null) return null; const n = beatsPerBar(map()!); return ((Math.floor(b) % n) + n) % n; };
  const dots = () => Array.from({ length: map() ? beatsPerBar(map()!) : 4 }, (_, i) => i);
  const playX = () => { const b = playBar(); return b == null ? null : xOfBar(b); };
  // Follow the playhead a page at a time once it leaves the view, unless the operator moved the view just now.
  createEffect(() => {
    const b = playBar();
    if (b == null || fitted() || Date.now() - lastUserView < 1500) return;
    if (b < from() || b > from() + span()) setView(b - 0.1 * span(), span());
  });

  // ---- the part menu ----
  const [menu, setMenu] = createSignal<{ index: number; x: number; y: number; fresh: boolean } | null>(null);
  const [other, setOther] = createSignal(false);
  const openMenu = (index: number, x: number, y: number, fresh: boolean) => { setOther(false); setSelFrom(parts()[index]?.from ?? null); setMenu({ index, x, y, fresh }); };
  /** Close it; a part just dragged out and never named goes, with anything it cut (it was never saved). */
  const dismissMenu = () => { if (menu()?.fresh) setPreview(null); setMenu(null); };
  const nameIt = (index: number, kind: string | null) => {
    const next = setName(parts(), index, kind);
    const at = parts()[index]?.from ?? null;
    setPreview(null); setMenu(null);
    save(next);
    setSelFrom(at);
  };
  /** Split the part under the playhead (or the cue) there. */
  const splitHere = () => {
    const b = Math.floor(playBar() ?? cueBar());
    const i = partAt(parts(), b);
    if (i < 0 || parts()[i]!.from === b) return;
    save(splitAt(parts(), b));
    setSelFrom(b);
    pushToast('ok', `Split at bar ${b}. Click the new part to name it.`, 2500);
  };

  // Keys once the row was touched: 1-5 name the chosen part, Space plays / stops, S splits, \ fits, Esc takes a drag
  // back or closes the menu. Ahead of the editor's own keys (Space = play the TIMELINE there), never while typing.
  let cancelDrag: (() => void) | null = null;
  {
    const onKey = (e: KeyboardEvent) => {
      if (!active() && !menu()) return;
      const t = e.target as HTMLElement | null;
      if (t?.closest('input, textarea, select, [contenteditable="true"]')) return;
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      if (document.querySelector('[data-testid="tl2-import"], .tl2-ctx')) return; // a window or a menu is over it
      const stopIt = () => { e.preventDefault(); e.stopPropagation(); };
      if (e.key === 'Escape') {
        stopIt();
        if (cancelDrag) { cancelDrag(); return; }
        if (menu()) { dismissMenu(); return; }
        setActive(false); setSelFrom(null);
        props.onDone?.();
        return;
      }
      const n = Number(e.key);
      if (Number.isInteger(n) && n >= 1 && n <= PART_KINDS.length) {
        const i = menu()?.index ?? selIndex();
        if (i >= 0) { stopIt(); nameIt(i, PART_KINDS[n - 1]!); }
        return;
      }
      if (e.key === ' ') { stopIt(); togglePlay(); return; }
      if (e.key === 's' || e.key === 'S') { stopIt(); splitHere(); return; }
      if (e.key === '\\') { stopIt(); fit(); }
    };
    const onDownAnywhere = (e: PointerEvent) => {
      const t = e.target as HTMLElement | null;
      if (t?.closest('[data-testid="tl2-pmenu"]')) return;
      if (menu()) dismissMenu();
      if (!rowEl?.contains(t) && !props.onDone) { setActive(false); }
    };
    onMount(() => { window.addEventListener('keydown', onKey, { capture: true }); document.addEventListener('pointerdown', onDownAnywhere, { capture: true }); });
    onCleanup(() => { window.removeEventListener('keydown', onKey, { capture: true }); document.removeEventListener('pointerdown', onDownAnywhere, { capture: true }); });
  }

  // Two fingers on the strip: pinch zooms about their middle, moving them scrolls. A second finger cancels whatever
  // the first one started, so a pinch never leaves a stray part behind.
  const touches = new Map<number, number>();
  let pinch: { d0: number; s0: number; anchor: number } | null = null;
  let gestureCancelled = false;
  const pinchMove = () => {
    if (!pinch || touches.size < 2 || !stripEl) return;
    const [a, b] = [...touches.values()] as [number, number];
    const r = stripEl.getBoundingClientRect();
    const d = Math.max(8, Math.abs(a - b));
    const mid = Math.max(0, Math.min(1, ((a + b) / 2 - r.left) / Math.max(1, r.width)));
    const s1 = pinch.s0 * (pinch.d0 / d);
    const clamped = Math.max(Math.min(MIN_SPAN, bars()), Math.min(bars(), s1));
    lastUserView = Date.now();
    setView(pinch.anchor - mid * clamped, clamped);
  };

  /** What is under viewport point (x, y) of the strip: a line between parts, a part, or the bar numbers. */
  const hitAt = (el: HTMLElement, clientX: number, clientY: number, touch: boolean) => {
    const r = el.getBoundingClientRect();
    if (clientY - r.top < H_BARS) return { what: 'bars' as const, index: -1 };
    const x = clientX - r.left;
    // A line's grab zone grows with the zoom: zoomed out (a bar is ~9 px across the whole song) a fixed 6 px took most
    // of every bar beside a line, so a drag meant to make a part moved the line instead (smoke, 2026-10-21).
    const reach = touch ? Math.max(6, Math.min(12, ppb() * 0.5)) : Math.max(3, Math.min(6, ppb() * 0.35));
    const ps = parts();
    let line = -1; let best = reach + 1;
    for (let i = 0; i < ps.length - 1; i++) { const d = Math.abs(xOfBar(ps[i]!.to + 1) - x); if (d <= reach && d < best) { best = d; line = i; } }
    if (line >= 0) return { what: 'line' as const, index: line };
    const i = partAt(ps, Math.floor(barAtX(el, clientX)));
    return { what: ps[i]?.name ? 'part' as const : 'none' as const, index: i };
  };

  function onDown(e: PointerEvent) {
    const m = map();
    if (!m || !bars()) return;
    setActive(true);
    if (e.pointerType === 'touch') {
      touches.set(e.pointerId, e.clientX);
      if (touches.size === 2 && stripEl) {
        gestureCancelled = true;
        cancelDrag?.();
        const [a, b] = [...touches.values()] as [number, number];
        const r = stripEl.getBoundingClientRect();
        const mid = ((a + b) / 2 - r.left) / Math.max(1, r.width);
        pinch = { d0: Math.max(8, Math.abs(a - b)), s0: span(), anchor: from() + mid * span() };
        return;
      }
      if (touches.size > 2) return;
    }
    if (e.button !== 0) return;
    if (menu()) dismissMenu();
    gestureCancelled = false;
    const el = e.currentTarget as HTMLElement;
    const hit = hitAt(el, e.clientX, e.clientY, e.pointerType === 'touch');
    try { el.setPointerCapture(e.pointerId); } catch { /* a pointer the browser no longer tracks: no capture */ }
    e.preventDefault();
    const x0 = e.clientX;
    const start = parts();
    let moved = false;
    const bar0 = barAtX(el, e.clientX);
    const menuY = el.getBoundingClientRect().bottom - 6;
    const move = (ev: PointerEvent) => {
      if (gestureCancelled) return;
      if (!moved && Math.abs(ev.clientX - x0) < 4) return;
      moved = true;
      const bar = barAtX(el, ev.clientX);
      if (hit.what === 'line') setPreview(moveLine(start, hit.index, Math.round(bar)));
      else if (hit.what === 'part') {
        const r = slide(start, hit.index, start[hit.index]!.from + Math.round(bar - bar0));
        setPreview(r.parts); setSelFrom(r.parts[r.index]?.from ?? null);
      } else if (hit.what === 'none') setDraft({ a: Math.floor(bar0), b: Math.max(1, Math.min(bars(), Math.floor(bar))) });
    };
    const end = () => {
      el.removeEventListener('pointermove', move);
      el.removeEventListener('pointerup', up);
      el.removeEventListener('pointercancel', cancel);
      cancelDrag = null;
    };
    const cancel = () => { end(); setPreview(null); setDraft(null); };
    const up = (ev: PointerEvent) => {
      end();
      if (gestureCancelled) { setPreview(null); setDraft(null); return; }
      if (!moved) {
        if (hit.what === 'part') openMenu(hit.index, ev.clientX, menuY, false);
        else playFrom(barAtX(el, ev.clientX));
        return;
      }
      if (hit.what === 'line' || hit.what === 'part') {
        const next = preview();
        setPreview(null);
        if (next && JSON.stringify(next) !== JSON.stringify(start)) save(next);
        else if (hit.what === 'part' && slideRange(start, hit.index).lo === slideRange(start, hit.index).hi) {
          pushToast('warn', 'It has a part on both sides: drag the line between them instead, or make room first.', 3500);
        }
        return;
      }
      const d = draft();
      setDraft(null);
      if (!d) return;
      const r = markRange(start, d.a, d.b, null);
      setPreview(r.parts);
      openMenu(r.index, ev.clientX, menuY, true);
    };
    cancelDrag = cancel;
    el.addEventListener('pointermove', move);
    el.addEventListener('pointerup', up);
    el.addEventListener('pointercancel', cancel);
  }
  const onStripMove = (e: PointerEvent) => {
    if (e.pointerType === 'touch') { if (touches.has(e.pointerId)) { touches.set(e.pointerId, e.clientX); pinchMove(); } return; }
    if (!map() || !bars()) return;
    const el = e.currentTarget as HTMLElement;
    setHoverBar(barAtX(el, e.clientX));
    setHoverWhat(hitAt(el, e.clientX, e.clientY, false).what);
  };
  const onStripEnd = (e: PointerEvent) => {
    if (e.pointerType !== 'touch') return;
    touches.delete(e.pointerId);
    if (touches.size < 2) pinch = null;
  };

  // ---- what is in view ----
  const labelEvery = () => LABEL_LADDER.find((n) => n * ppb() >= LABEL_MIN_PX) ?? 128;
  const visibleBars = createMemo(() => {
    const b = bars();
    if (!b) return [] as number[];
    const a = Math.max(0, Math.floor(from() - 1));
    const z = Math.min(b, Math.ceil(from() - 1 + span()) + 1);
    return Array.from({ length: Math.max(0, z - a) }, (_, i) => a + i);
  });
  /** Beat lines once a beat is at least 12 px wide. */
  const beatLines = () => {
    const m = map();
    if (!m) return [] as number[];
    const n = beatsPerBar(m);
    if (ppb() / n < 12) return [] as number[];
    return visibleBars().flatMap((i) => Array.from({ length: n - 1 }, (_, k) => i + (k + 1) / n));
  };
  const wavePath = createMemo(() => {
    const m = map(); const p = peaks();
    if (!m || !p || !bars()) return '';
    return waveformPath(p, songTimeAtBar(m, from()), songTimeAtBar(m, from() + span()), props.width, H_WAVE);
  });
  /** A band's label stays readable at the left edge when the band starts off screen. */
  const labelPad = (left: number) => `${Math.max(4, 4 - left)}px`;

  // ---- the overview: the whole song in the strip's width, the view as a window ----
  const ovX = (bar: number) => (bars() ? ((bar - 1) / bars()) * props.width : 0);
  function onOverviewDown(e: PointerEvent) {
    if (!bars() || e.button !== 0) return;
    e.preventDefault();
    e.stopPropagation();
    const el = e.currentTarget as HTMLElement;
    el.setPointerCapture(e.pointerId);
    const left = el.getBoundingClientRect().left;
    const barOf = (x: number) => 1 + ((x - left) / Math.max(1, props.width)) * bars();
    const insideWindow = (() => { const b = barOf(e.clientX); return b >= from() && b <= from() + span(); })();
    // A press outside the window centres it there first; then dragging scrolls.
    if (!insideWindow) setView(barOf(e.clientX) - span() / 2, span());
    const grabOffset = barOf(e.clientX) - from();
    lastUserView = Date.now();
    const move = (ev: PointerEvent) => { lastUserView = Date.now(); setView(barOf(ev.clientX) - grabOffset, span()); };
    const up = () => { el.removeEventListener('pointermove', move); el.removeEventListener('pointerup', up); el.removeEventListener('pointercancel', up); };
    el.addEventListener('pointermove', move);
    el.addEventListener('pointerup', up);
    el.addEventListener('pointercancel', up);
  }

  const menuPart = () => { const m = menu(); return m ? parts()[m.index] : undefined; };
  const named = () => parts().filter((p) => p.name).length;
  const totalH = H_BARS + H_ENERGY + H_WAVE;
  return (
    <div class="tl2-row tl2-song" data-testid="tl2-song" data-active={active() ? 'true' : undefined} ref={rowEl}>
      <div class="tl2-h tl2-song-h">
        <div class="tl2-song-title"><b>Song parts</b><span>{named() ? `${named()} part${named() === 1 ? '' : 's'}` : 'none yet'}</span></div>
        <div class="tl2-song-help">Drag across the song, then say what that part is. Drag a part to move it. Click to listen.</div>
        <div class="tl2-song-meta">
          <span class="tl2-song-metro" data-playing={beatInBar() != null ? 'true' : 'false'} aria-label="Beat" title="The song's beat while it plays">
            <For each={dots()}>{(i) => <i data-active={beatInBar() === i ? 'true' : 'false'} data-one={i === 0 ? 'true' : undefined} />}</For>
          </span>
          <span class="tl2-song-key"><i class="tl2-song-key-vox" />Vocals<i class="tl2-song-key-en" />Loudness</span>
        </div>
        <div class="tl2-song-acts">
          <button type="button" class="tl2-btn" data-testid="tl2-song-play" onClick={togglePlay} title={playingHere() ? 'Stop (Space)' : `Play from bar ${Math.floor(cueBar())} (Space)`}>{playingHere() ? 'Stop' : 'Play'}</button>
          <button type="button" class="tl2-btn" data-testid="tl2-song-fit" disabled={fitted()} onClick={fit} title="Show the whole song (\)">Fit</button>
          <Show when={props.onDone}><button type="button" class="tl2-btn" onClick={() => props.onDone!()}>Done</button></Show>
          <Show when={props.onHide}><button type="button" class="tl2-btn" data-testid="tl2-song-hide" onClick={() => props.onHide!()} title="Fold the song's parts to a thin strip">Hide</button></Show>
        </div>
      </div>
      <div class="tl2-c tl2-song-c" style={{ width: `${props.width}px`, height: `${totalH + H_OVERVIEW}px` }}
        data-info="Drag across the song to make a part, then say what it is. Drag a part to move it, or the line between two parts. Click a part for more; click unmarked song or the bar numbers to listen. Wheel zooms, Shift+wheel scrolls, \ fits.">
        <div class="tl2-song-in" ref={stripEl} data-from={from().toFixed(3)} data-span={span().toFixed(3)} data-cursor={hoverWhat()}
          style={{ width: `${props.width}px`, height: `${totalH}px` }}
          onPointerDown={onDown} onPointerMove={onStripMove} onPointerUp={onStripEnd} onPointerCancel={onStripEnd}
          onPointerLeave={() => setHoverBar(null)}>
        <Show when={map()} fallback={<span class="tl2-trk-hint">{map() === null ? 'This song is not analysed yet.' : 'Loading the song...'}</span>}>
          {/* beat lines (zoomed in), phrase lines and bar numbers */}
          <For each={beatLines()}>{(b) => <i class="tl2-song-beat" style={{ left: `${xOfBar(b + 1)}px` }} />}</For>
          <For each={visibleBars()}>
            {(i) => (
              <>
                <Show when={i % 4 === 0 || ppb() >= 40}><i class="tl2-song-ph" data-strong={i % 16 === 0 ? 'true' : undefined} data-bar={i % 4 !== 0 ? 'true' : undefined} style={{ left: `${xOfBar(i + 1)}px` }} /></Show>
                <Show when={i % labelEvery() === 0}><b class="tl2-song-bn" style={{ left: `${xOfBar(i + 1)}px` }}>{i + 1}</b></Show>
              </>
            )}
          </For>
          {/* energy per bar, and where someone sings */}
          <svg class="tl2-song-energy" style={{ top: `${H_BARS}px`, height: `${H_ENERGY}px`, width: `${props.width}px` }} viewBox={`0 0 ${Math.max(1, props.width)} ${H_ENERGY}`} preserveAspectRatio="none" aria-hidden="true">
            <For each={(map()!.vocals ?? [])}>
              {(v) => <rect class="tl2-song-vox" x={xOfSec(v.start)} y="0" width={Math.max(1, xOfSec(v.end) - xOfSec(v.start))} height={H_ENERGY}><title>Vocals: the analyser hears singing here</title></rect>}
            </For>
            <For each={visibleBars().filter((i) => i < (map()!.bar_energy ?? []).length)}>
              {(i) => { const e = () => map()!.bar_energy![i]!; return <rect class="tl2-song-en" x={xOfBar(i + 1)} y={H_ENERGY - Math.max(1, e() * (H_ENERGY - 2))} width={Math.max(1, ppb() - 1)} height={Math.max(1, e() * (H_ENERGY - 2))} />; }}
            </For>
          </svg>
          <svg class="tl2-wave tl2-song-wave" style={{ left: '0px', top: `${H_BARS + H_ENERGY}px`, height: `${H_WAVE}px`, width: `${props.width}px` }} viewBox={`0 0 ${Math.max(1, props.width)} ${H_WAVE}`} preserveAspectRatio="none">
            <path d={wavePath()} />
          </svg>
          <For each={parts()}>
            {(p, i) => {
              const left = () => xOfBar(p.from);
              const w = () => (p.to - p.from + 1) * ppb();
              return (
                <span
                  class="tl2-mark"
                  classList={{ 'tl2-mark-none': !p.name }}
                  data-mark={p.name ?? ''}
                  data-from={p.from}
                  data-to={p.to}
                  aria-current={i() === selIndex() ? 'true' : undefined}
                  style={{ left: `${left()}px`, width: `${w()}px`, '--hue': hueOf(p.name), 'padding-left': labelPad(left()) }}
                ><em>{p.name ?? (w() > 150 ? 'Not a part yet: drag across it' : '')}</em><small>{w() > 50 ? `${p.from}-${p.to}` : ''}</small></span>
              );
            }}
          </For>
          <Show when={draft()}>
            {(d) => {
              const lo = () => Math.min(d().a, d().b); const hi = () => Math.max(d().a, d().b);
              const left = () => xOfBar(lo());
              return <span class="tl2-mark tl2-mark-draft" style={{ left: `${left()}px`, width: `${(hi() - lo() + 1) * ppb()}px`, 'padding-left': labelPad(left()) }}><em>New part</em><small>{lo()}-{hi()}: {hi() - lo() + 1} bars</small></span>;
            }}
          </Show>
          <Show when={hoverBar() != null && !draft() && !preview()}>
            <i class="tl2-song-hover" style={{ left: `${xOfBar(hoverBar()!)}px` }}><b>{Math.floor(hoverBar()!)}</b></i>
          </Show>
          <Show when={playX() != null}><i class="tl2-song-playhead" style={{ left: `${playX()}px` }} /></Show>
        </Show>
        </div>
        <Show when={map()}>
          <div class="tl2-song-ov" data-testid="tl2-song-ov" style={{ top: `${totalH}px`, width: `${props.width}px`, height: `${H_OVERVIEW}px` }}
            onPointerDown={onOverviewDown} title="The whole song: drag the window to scroll, click to jump">
            <For each={parts().filter((p) => p.name)}>
              {(m) => <span class="tl2-song-ov-mk" style={{ left: `${ovX(m.from)}px`, width: `${Math.max(1, ovX(m.to + 1) - ovX(m.from))}px`, '--hue': hueOf(m.name) }} />}
            </For>
            <Show when={playBar() != null}><i class="tl2-song-ov-ph" style={{ left: `${ovX(playBar()!)}px` }} /></Show>
            <span class="tl2-song-ov-win" data-fitted={fitted() ? 'true' : undefined} style={{ left: `${ovX(from())}px`, width: `${Math.max(6, ovX(from() + span()) - ovX(from()))}px` }} />
          </div>
        </Show>
      </div>
      <Show when={menu() && menuPart()}>
        <Portal>
          <div class="tl2-pmenu" data-testid="tl2-pmenu" data-nav-layer role="dialog" aria-label="This part"
            style={{ left: `${Math.max(8, Math.min(window.innerWidth - 268, menu()!.x - 24))}px`, top: `${Math.max(8, Math.min(window.innerHeight - 300, menu()!.y))}px` }}>
            <div class="tl2-pmenu-t">{menu()!.fresh ? 'What is this part?' : (menuPart()!.name ?? 'Not a part yet')}<small>bars {menuPart()!.from}-{menuPart()!.to}</small></div>
            <div class="tl2-pmenu-kinds">
              <For each={[...PART_KINDS]}>
                {(k, i) => (
                  <button type="button" data-kind={k} aria-pressed={!!menuPart()!.name && kindOf(menuPart()!.name!) === k} style={{ '--hue': sectionHue(k) }}
                    onClick={() => nameIt(menu()!.index, k)}>{label(k)}<kbd>{i() + 1}</kbd></button>
                )}
              </For>
              <Show when={other()} fallback={<button type="button" class="tl2-pmenu-other" data-testid="tl2-pmenu-other" onClick={() => setOther(true)}>Other...</button>}>
                {(() => {
                  const listId = `tl2-parts-${Math.random().toString(36).slice(2, 8)}`;
                  const ok = (v: string) => { const n = v.trim(); if (n) nameIt(menu()!.index, n); };
                  return (
                    <div class="tl2-pmenu-in">
                      <input list={listId} placeholder="SOLO, OUTRO..." aria-label="Another kind of part" data-testid="tl2-pmenu-other-in"
                        ref={(el) => queueMicrotask(() => el.focus())}
                        onKeyDown={(e) => { e.stopPropagation(); if (e.key === 'Enter') ok(e.currentTarget.value); if (e.key === 'Escape') { e.preventDefault(); dismissMenu(); } }} />
                      <datalist id={listId}><For each={[...OTHER_KINDS]}>{(x) => <option value={x} />}</For></datalist>
                    </div>
                  );
                })()}
              </Show>
            </div>
            <Show when={!menu()!.fresh}>
              <div class="tl2-pmenu-acts">
                <button type="button" onClick={() => { const p = menuPart()!; setMenu(null); playFrom(p.from, { fromBar: p.from, toBar: p.to }); }}>Play it, looping</button>
                <button type="button" disabled={!((playBar() ?? 0) > menuPart()!.from && (playBar() ?? 0) < menuPart()!.to + 1)} onClick={() => { setMenu(null); splitHere(); }}>Split at the playhead<small>S</small></button>
                <button type="button" disabled={menu()!.index === 0} onClick={() => { const i = menu()!.index; setMenu(null); save(join(parts(), i, -1)); }}>Join with the part before</button>
                <button type="button" disabled={menu()!.index >= parts().length - 1} onClick={() => { const i = menu()!.index; setMenu(null); save(join(parts(), i, 1)); }}>Join with the part after</button>
                <button type="button" data-testid="tl2-pmenu-clear" disabled={!menuPart()!.name} onClick={() => nameIt(menu()!.index, null)}>Not a part</button>
              </div>
            </Show>
          </div>
        </Portal>
      </Show>
    </div>
  );
}
